import Card from 'components/card';
import SearchIcon from 'components/icons/SearchIcon';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getArchivedProducts, unarchiveProduct } from 'interfaces/products';
import GoBack from 'components/actions/GoBack';
import { MdUnarchive } from 'react-icons/md';

const ArchivedProducts = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [archivedProducts, setArchivedProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const initArchivedProducts = async () => {
        setLoading(true);
        const res = await getArchivedProducts();
        if (res.error) {
            navigate('/auth/login');
            return;
        }
        setArchivedProducts(res.data || []);
        setLoading(false);
    };

    useEffect(() => {
        initArchivedProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUnarchive = async (productId: string) => {
        if (!window.confirm('Êtes-vous sûr de vouloir désarchiver ce produit ?')) {
            return;
        }
        
        const res = await unarchiveProduct(productId);
        if (res.error) {
            alert('Erreur lors de la désarchivage du produit');
            return;
        }
        
        // Refresh the list
        initArchivedProducts();
    };

    const searchProduct = (product: any, search: string) => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        const name = typeof product.name === 'string' ? product.name.toLowerCase() : '';
        const ref = typeof product.ref === 'string' ? product.ref.toLowerCase() : '';
        return name.includes(searchLower) || ref.includes(searchLower);
    };

    const filteredProducts = archivedProducts.filter((product) =>
        searchProduct(product, search)
    );

    return (
        <>
            <GoBack />
            <Card extra={'w-full h-full sm:overflow-auto p-5 border mt-5'}>
                <div className="mb-5">
                    <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
                        Produits archivés
                    </h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Liste de tous les produits archivés. Vous pouvez les désarchiver pour les rendre à nouveau visibles.
                    </p>
                </div>

                {/* Search bar */}
                <div className="mb-5 flex h-[38px] flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600 dark:!bg-navy-900 dark:text-white">
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="Rechercher par nom ou référence..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                    />
                </div>

                {/* Loading state */}
                {loading && (
                    <div className="flex items-center justify-center py-10">
                        <div className="text-gray-600 dark:text-gray-400">
                            Chargement...
                        </div>
                    </div>
                )}

                {/* Empty state */}
                {!loading && archivedProducts.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-10">
                        <p className="text-gray-600 dark:text-gray-400">
                            Aucun produit archivé
                        </p>
                    </div>
                )}

                {/* Products table */}
                {!loading && archivedProducts.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-gray-700 bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-4 py-3">
                                        Produit
                                    </th>
                                    <th scope="col" className="px-4 py-3">
                                        Référence
                                    </th>
                                    <th scope="col" className="px-4 py-3">
                                        Type
                                    </th>
                                    <th scope="col" className="px-4 py-3">
                                        Unité
                                    </th>
                                    <th scope="col" className="px-4 py-3">
                                        Prix
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-center">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map((product: any) => (
                                    <tr
                                        key={product._id}
                                        className="bg-white border-b hover:bg-gray-50 dark:bg-navy-800 dark:border-navy-700 dark:hover:bg-navy-700"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                {product.imageUrl && (
                                                    <img
                                                        src={product.imageUrl}
                                                        alt={product.name}
                                                        className="h-10 w-10 rounded object-cover"
                                                    />
                                                )}
                                                <span className="font-medium text-gray-900 dark:text-white">
                                                    {product.name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                            {product.ref || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                            {product.productType === 'brut'
                                                ? 'Produit brut'
                                                : product.productType === 'transformed'
                                                ? 'Produit transformé'
                                                : product.productType === 'assembled'
                                                ? 'Produit assemblé'
                                                : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                            {product.unity || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                            {product.price !== undefined && product.price !== null
                                                ? `${Number(product.price).toFixed(2)}€`
                                                : '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => handleUnarchive(product._id)}
                                                    className="flex items-center gap-1 rounded-lg bg-green-500 px-3 py-2 text-sm font-medium text-white hover:bg-green-600 transition-colors"
                                                    title="Désarchiver"
                                                >
                                                    <MdUnarchive size={18} />
                                                    Désarchiver
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {filteredProducts.length === 0 && search && (
                            <div className="p-6 text-center text-gray-500">
                                Aucun produit trouvé pour "{search}"
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </>
    );
};

export default ArchivedProducts;
